import { css } from '@emotion/react'
import { Heading, Text, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { Cid10 } from 'graphql/types.generated'
import { porExtenso } from 'numero-por-extenso'
import { conformToMask } from 'react-text-mask'
import { formatDate } from 'util/date/formatDate'
import { masks } from 'util/mask'

import AtestadoModel, { CidadaoAtestado } from '../components/model-atestado'
import { buildCid10Substring, buildNomeSubstring } from '../utils-atestado'

interface AtestadoPadraoTextBoxProps {
  cidadao: CidadaoAtestado
  values: AtestadoModel
  nomeUnidadeSaude: string
}

export default function AtestadoPadraoTextBox(props: AtestadoPadraoTextBoxProps) {
  return (
    <Box>
      <VFlow
        vSpacing={0.5}
        style={css`
          justify-content: center;
          align-items: center;
          margin: 1rem 6rem 2rem;
        `}
      >
        <Heading level={3}>ATESTADO</Heading>
        <Text>{getAtestadoPadraoText(props)}</Text>
      </VFlow>
    </Box>
  )
}

const renderCpfCnsSubString = (cidadao: CidadaoAtestado, cpfCns: string, naoPossui: boolean): string => {
  const getCpfMasked = (cpf: string) => conformToMask(cpf, masks.cpf, { guide: false }).conformedValue

  if (cidadao.cpf) {
    return `CPF ${getCpfMasked(cidadao.cpf)}, `
  }

  if (cidadao.cns) {
    return `CNS ${cidadao.cns}, `
  }

  if (naoPossui) {
    return ''
  }

  if (!cpfCns || (cpfCns.length !== 11 && cpfCns.length !== 15)) {
    return '[CNS/CPF], '
  }

  if (cpfCns.length === 11) {
    return `CPF ${getCpfMasked(cpfCns)}, `
  }

  if (cpfCns.length === 15) {
    return `CNS ${cpfCns}, `
  }
}

const renderData = (data: LocalDate | undefined): string => {
  return (data && formatDate(data)) || '[DATA]'
}

const renderHora = (hora: string): string => {
  return hora || '[HORA]'
}

const renderDias = (dias: number) => {
  const diasLabel = dias?.toString() === '1' ? 'dia' : 'dias'
  const diasPorExtenso = dias && `${dias} (${porExtenso(dias)}) ${diasLabel}`
  return diasPorExtenso || '[DIAS]'
}

export const getAtestadoPadraoText = (props: AtestadoPadraoTextBoxProps): string => {
  const { cidadao, nomeUnidadeSaude, values } = props
  return atestadoPadraoText({
    nomeUnidadeSaude,
    cidadao,
    cpfCns: values.cpfCns,
    data: renderData(values.data),
    hora: values.hora,
    cid10: values.cid10,
    dias: values.dias,
  })
}

export const atestadoPadraoText = (props: {
  nomeUnidadeSaude: string
  cidadao: CidadaoAtestado
  cpfCns: string
  data: string
  hora: string
  cid10: Cid10
  dias: number
}) => {
  const { nomeUnidadeSaude, cidadao, cpfCns, data, hora, cid10, dias } = props
  const nomeCidadao = buildNomeSubstring(cidadao)
  const naoPossui = !cidadao.cpf && !cidadao.cns && !cpfCns

  return `Atesto, para os devidos fins, que ${nomeCidadao}, ${renderCpfCnsSubString(
    cidadao,
    cpfCns,
    naoPossui
  )}recebeu atendimento na ${nomeUnidadeSaude} no dia ${data} às ${renderHora(hora)}${buildCid10Substring(
    cid10
  )}. Em decorrência, deverá permanecer em afastamento de suas atividades laborativas por um período de ${renderDias(
    dias
  )} a partir desta data.`
}
